import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * ProtectedRoute Component
 * 
 * Protects routes based on authentication and permissions.
 * 
 * Usage:
 * <Route path="/users" element={<ProtectedRoute permission={PERMISSIONS.RBAC_USER_MANAGEMENT_VIEW}><UsersPage /></ProtectedRoute>} />
 * 
 * Props:
 * - permission: Single permission string (optional)
 * - permissions: Array of permissions - user needs ANY (optional)
 * - requireAll: If true with permissions array, user needs ALL permissions
 * - children: Component to render if authorized
 * - redirectTo: Custom redirect path (default: '/unauthorized')
 */
const ProtectedRoute = ({
  permission,
  permissions,
  requireAll = false,
  superAdminOnly = false,
  children,
  redirectTo = '/unauthorized',
}) => {
  const { isAuthenticated, hasPermission, hasAnyPermission, hasAllPermissions, isSuperAdmin } = useAuth();
  const location = useLocation();

  // Check authentication first
  if (!isAuthenticated) {
    // Redirect to login, preserve intended location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (superAdminOnly) {
    if (!isSuperAdmin) {
      return <Navigate to={redirectTo} state={{ from: location }} replace />;
    }
    return <>{children}</>;
  }

  // Super Admin bypasses all permission checks
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // Check permissions
  let isAuthorized = true;

  if (permission) {
    // Single permission check
    isAuthorized = hasPermission(permission);
  } else if (permissions && permissions.length > 0) {
    // Multiple permissions check
    if (requireAll) {
      // User needs ALL permissions
      isAuthorized = hasAllPermissions(permissions);
    } else {
      // User needs ANY permission
      isAuthorized = hasAnyPermission(permissions);
    }
  }

  // If not authorized, redirect
  if (!isAuthorized) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // User is authorized, render children
  return <>{children}</>;
};

export default ProtectedRoute;
